import React, { useEffect, useRef, useState, useMemo } from 'react';

/**
 * Venn3Input
 * value: number[]              // maski 1..7 (bity: 1=A, 2=B, 4=C)
 * onChange(nextValue:number[]): void
 * disabled?: boolean
 * ariaLabel?: string
 * size?: number                // szerokość w px
 * ghostBackground?: boolean
 * theme?: 'auto'|'light'|'dark'
 * outlineColor?: string
 * labelColor?: string
 * paintColor?: string
 * pairDistanceRatio?: number   // s = pairDistanceRatio * r  (im mniejsze, tym większy środek)
 * radiusScale?: number         // 0–1; skala promienia (domyślnie 0.90)
 * margin?: number              // wewnętrzny margines na boki (domyślnie ~3.5% width, min 8)
 * labelFontPx?: number         // rozmiar fontu etykiet A/B/C (domyślnie 14)
 * labelGap?: number            // odstęp etykiety od okręgu (domyślnie 10)
 */
export default function Venn3Input({
  value,
  onChange,
  disabled = false,
  ariaLabel = 'diagram Venna A, B, C',
  size = 360,
  ghostBackground = false,
  theme = 'auto',
  outlineColor,
  labelColor: labelColorProp,
  paintColor = '#FF9800',
  pairDistanceRatio = 1.35,
  radiusScale = 0.90,
  margin,
  labelFontPx = 14,
  labelGap = 10,
}) {
  const canvasRef = useRef(null);
  const [hoverMask, setHoverMask] = useState(0);

  // ——— dynamiczny auto-motyw
  const [autoTheme, setAutoTheme] = useState('light');
  const resolveThemeNow = () => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      if (root.classList.contains('dark')) return 'dark';
      if (root.classList.contains('light')) return 'light';
      const dataTheme = root.dataset?.theme;
      if (dataTheme === 'dark' || dataTheme === 'light') return dataTheme;
    }
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      return 'dark';
    }
    return 'light';
  };
  useEffect(() => {
    if (theme !== 'auto') return;
    setAutoTheme(resolveThemeNow());

    let mo;
    try {
      const target = document.documentElement;
      mo = new MutationObserver(() => setAutoTheme(resolveThemeNow()));
      mo.observe(target, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    } catch {}

    let mql, handler;
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      mql = window.matchMedia('(prefers-color-scheme: dark)');
      handler = () => setAutoTheme(resolveThemeNow());
      if (typeof mql.addEventListener === 'function') mql.addEventListener('change', handler);
      else if (typeof mql.addListener === 'function') mql.addListener(handler);
    }

    return () => {
      if (mo) mo.disconnect();
      if (mql && handler) {
        if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', handler);
        else if (typeof mql.removeListener === 'function') mql.removeListener(handler);
      }
    };
  }, [theme]);

  const resolvedTheme = theme === 'auto' ? autoTheme : theme;

  // wejście → Set
  const selected = useMemo(() => {
    const arr = Array.isArray(value) ? value : [];
    const ok = arr
      .map((m) => (typeof m === 'string' ? parseInt(m, 10) : m))
      .filter((m) => Number.isInteger(m) && m >= 1 && m <= 7);
    return new Set(ok);
  }, [value]);

  const dpr = Math.max(1, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

  const strokeColor =
    outlineColor ??
    (resolvedTheme === 'dark' ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.92)');
  const labelColor =
    labelColorProp ??
    (resolvedTheme === 'dark' ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.92)');

  // ——— GEOMETRIA: rezerwujemy przestrzeń na etykiety, więc nic się nie przytnie
  const geom = useMemo(() => {
    const w = size;
    const m = Math.max(8, margin ?? Math.round(size * 0.035));

    const k = Math.min(1.95, Math.max(0.9, pairDistanceRatio));      // s = k * r
    const rBase = Math.floor((w - 2 * m) / (2 + k));                  // dopasuj do szerokości
    const r = Math.max(12, Math.floor(rBase * Math.min(1, Math.max(0.5, radiusScale))));

    const s = k * r;

    // ile potrzebujemy pionowo na etykiety?
    const topLabelSpace = labelFontPx + labelGap;     // nad okręgami A/B
    const bottomLabelSpace = labelFontPx + labelGap;  // pod okręgiem C

    // pionowy span kół: 2r + (sqrt(3)/2)*s
    const circlesSpan = 2 * r + (Math.sqrt(3) / 2) * s;

    // ostateczna wysokość: górna etykieta + margines + koła + margines + dolna etykieta
    const h = Math.round(topLabelSpace + m + circlesSpan + m + bottomLabelSpace);

    // pozycje środków:
    // górne koła (A/B) opieramy o topLabelSpace + m
    const topY = topLabelSpace + m + r;
    const cyA = Math.round(topY);
    const cyB = Math.round(topY);
    const cyC = Math.round(topY + (Math.sqrt(3) / 2) * s);

    // w poziomie: równobocznie, środek kompozycji pośrodku szerokości
    const cx = w / 2;
    const dx = s / 2;
    const cxA = Math.round(cx - dx);
    const cxB = Math.round(cx + dx);
    const cxC = Math.round(cx);

    return {
      w, h,
      cxA, cyA, cxB, cyB, cxC, cyC,
      r, m, s, k,
      topLabelSpace, bottomLabelSpace,
    };
  }, [size, margin, pairDistanceRatio, radiusScale, labelFontPx, labelGap]);

  // hit-test regionów 1..7
  const hitTest = (x, y) => {
    const { cxA, cyA, cxB, cyB, cxC, cyC, r } = geom;
    const inA = (x - cxA) ** 2 + (y - cyA) ** 2 <= r ** 2;
    const inB = (x - cxB) ** 2 + (y - cyB) ** 2 <= r ** 2;
    const inC = (x - cxC) ** 2 + (y - cyC) ** 2 <= r ** 2;
    return (inA ? 1 : 0) | (inB ? 2 : 0) | (inC ? 4 : 0);
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { w, h, cxA, cyA, cxB, cyB, cxC, cyC, r } = geom;

    // DPI
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // tło
    ctx.clearRect(0, 0, w, h);

    // pomocnicze
    const circle = (c, x, y) => {
      c.beginPath();
      c.arc(x, y, r, 0, Math.PI * 2);
      c.closePath();
    };

    // delikatne „ghost” tło kół
    if (ghostBackground) {
      ctx.save();
      ctx.globalAlpha = resolvedTheme === 'dark' ? 0.08 : 0.06;
      ctx.fillStyle = resolvedTheme === 'dark' ? '#ffffff' : '#000000';
      circle(ctx, cxA, cyA); ctx.fill();
      circle(ctx, cxB, cyB); ctx.fill();
      circle(ctx, cxC, cyC); ctx.fill();
      ctx.restore();
    }

    // malowanie regionów (offscreen)
    const PAINT_COLOR = paintColor;
    const paintMask = (mask, alpha) => {
      if (!mask) return;
      const off = document.createElement('canvas');
      off.width = Math.round(w * dpr);
      off.height = Math.round(h * dpr);
      const octx = off.getContext('2d');
      octx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // 1) zalewamy offscreen
      octx.globalAlpha = alpha;
      octx.globalCompositeOperation = 'source-over';
      octx.fillStyle = PAINT_COLOR;
      octx.fillRect(0, 0, w, h);

      // 2) przycinanie do included
      const include = [];
      if (mask & 1) include.push([cxA, cyA]);
      if (mask & 2) include.push([cxB, cyB]);
      if (mask & 4) include.push([cxC, cyC]);
      include.forEach(([x, y]) => {
        octx.globalCompositeOperation = 'destination-in';
        circle(octx, x, y);
        octx.fill();
      });

      // 3) wycinanie excluded
      const exclude = [];
      if (!(mask & 1)) exclude.push([cxA, cyA]);
      if (!(mask & 2)) exclude.push([cxB, cyB]);
      if (!(mask & 4)) exclude.push([cxC, cyC]);
      exclude.forEach(([x, y]) => {
        octx.globalCompositeOperation = 'destination-out';
        circle(octx, x, y);
        octx.fill();
      });

      // 4) rzut na główny
      ctx.drawImage(off, 0, 0, off.width, off.height, 0, 0, w, h);
    };

    // hover → zaznaczenia
    if (!disabled && hoverMask >= 1 && hoverMask <= 7 && !selected.has(hoverMask)) {
      paintMask(hoverMask, 0.38);
    }
    selected.forEach((m) => paintMask(m, 1.0));

    // obrysy
    ctx.lineWidth = 2;
    ctx.strokeStyle = strokeColor;
    circle(ctx, cxA, cyA); ctx.stroke();
    circle(ctx, cxB, cyB); ctx.stroke();
    circle(ctx, cxC, cyC); ctx.stroke();

    // etykiety (nie przytną się, bo mamy zarezerwowane miejsce)
    ctx.fillStyle = labelColor;
    ctx.textAlign = 'center';
    ctx.font = `600 ${labelFontPx}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`;
    ctx.fillText('A', cxA, cyA - r - labelGap);
    ctx.fillText('B', cxB, cyB - r - labelGap);
    ctx.fillText('C', cxC, cyC + r + labelGap);
  };

  useEffect(() => {
    draw();
  }, [
    geom,
    selected,
    hoverMask,
    dpr,
    resolvedTheme,
    ghostBackground,
    paintColor,
    strokeColor,
    labelColor,
    labelFontPx,
    labelGap,
  ]);

  const handleMove = (e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    setHoverMask(hitTest(x, y));
  };
  const handleLeave = () => setHoverMask(0);

  const toggleMask = (m) => {
    if (disabled || m === 0) return;
    const next = new Set(selected);
    next.has(m) ? next.delete(m) : next.add(m);
    onChange?.(Array.from(next).sort((a, b) => a - b));
  };
  const handleClick = (e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    toggleMask(hitTest(x, y));
  };

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={ariaLabel}
      width={geom.w * dpr}
      height={geom.h * dpr}
      style={{
        width: `${geom.w}px`,
        height: `${geom.h}px`,
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: 12,
        background: 'transparent',
      }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={handleClick}
    />
  );
}
